import React, { useMemo } from 'react'
import { makeStyles } from '@mui/styles'

import { ButtonIconSquare } from '@/modules/base/components/buttons'
import { CLIENT_INVITATION_STATUS } from '@/modules/client/_helpers/getStatusLabel'
import { TaxConsultantService } from '@/modules/settings/components/tax-consultant/taxConsultantService'
import ThemeInterface from '@/theme-interface'
import lang from '@/lang'
import { toast } from '@/modules/base/components/toast/toastConfig.js'

interface ClientInvitationItemProps {
  client: {
    name: string
    email: string
    address: {
      street: string
      zipCode: string
      city: string
    }
    userName: string
    tenantCollaborationId: string
    isInviter: boolean
    clerks: string
  }
  isTaxConsultant: boolean
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  actions: {
    display: 'flex',
    alignItems: 'center',
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 10,
  },
  client: {
    backgroundColor: theme.backgroundColor,
    border: theme.border,
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    padding: 30,
  },
  companyName: {
    fontSize: theme.fontSizes.large,
    margin: 0,
  },
  address: {
    margin: 0,
  },
}))

const ClientInvitationItem = ({ client, isTaxConsultant = false }: ClientInvitationItemProps) => {
  const { name, email, address, tenantCollaborationId, userName, isInviter } = client
  const classes = useStyles()
  const buttons = useMemo(() => {
    if (isInviter) {
      return [
        {
          label: lang.taxConsultant.invitationsModal.cancel,
          variant: 'outline',
          onClick: () => handleStatusChange(tenantCollaborationId, CLIENT_INVITATION_STATUS.REJECTED),
        },
        {
          label: lang.taxConsultant.invitationsModal.resend,
          onClick: () => handleResend(tenantCollaborationId),
        },
      ]
    }
    return [
      {
        label: lang.taxConsultant.invitationsModal.reject,
        variant: 'outline',
        onClick: () => handleStatusChange(tenantCollaborationId, CLIENT_INVITATION_STATUS.REJECTED),
      },
      {
        label: lang.taxConsultant.invitationsModal.accept,
        onClick: () => handleStatusChange(tenantCollaborationId, CLIENT_INVITATION_STATUS.ACCEPTED),
      },
    ]
  }, [isInviter])

  const handleStatusChange = (id, status) => {
    TaxConsultantService.setConnectionStatus(id, status)
  }

  const handleResend = (id) => {
    TaxConsultantService.resendCollaborationInvitation(id).then(() =>
      toast.showToast({ message: lang.taxConsultantControl.pending.resentInvitation }))
  }

  return (
    <div className={classes.client}>
      <div>
        <p className={classes.companyName}>{name || email}</p>
        {isTaxConsultant
          // shows the name of the client user
          ? <p className={classes.address}>{userName || '-'}</p>
          // shows the assigned clerks in the widget of a client
          : <p className={classes.address}>{client?.clerks || '-'}</p>
        }
        <p className={classes.address}>{address?.street || '-'}</p>
        <p className={classes.address}>{address?.zipCode || '-'} {address?.city}</p>
        <p className={classes.address}>{name && email}</p>
      </div>
      <div className={classes.actions}>
        <div className={classes.buttons}>
          { buttons.map(({ label, variant, onClick }) => (
            <ButtonIconSquare
              key={label}
              label={label}
              variant={variant}
              onClick={onClick}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default ClientInvitationItem
